import { Checkbox, InputNumber, Table, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useSelector } from 'react-redux';
import { formatNumber, formatToDateOnly, toastText } from 'utils/utils';
import { useState } from 'react';
import { DeleteActionSvg } from 'utils/svgs';
import { payrollApi } from 'Api/payroll';
import ConfirmDelete from 'components/Global/confirmDeleteModel';

type Props = {
	isLoading: boolean;
	handleCheckboxChange: (id: string, value: boolean, name: string) => void;
	handlePaidLeaveDaysChange?: (employeeId: string, paidDays: number) => void;
	employeeData: any;
	onlyView: boolean;
	tableChangeHandler: (pagination: any, filters: any, sorter: any) => void;
	handleBulkCheckboxChange: (
		type: 'leave' | 'gratuity',
		value: boolean,
	) => void;

	showSteps: boolean;
	fetchLeaveGratuityData: (id: string) => void;
};

const LeaveGratuityPayTable = (props: Props) => {
	const {
		isLoading,
		onlyView,
		handleCheckboxChange,
		handlePaidLeaveDaysChange,
		employeeData,
		tableChangeHandler,
		showSteps,
		handleBulkCheckboxChange,
		fetchLeaveGratuityData,
	} = props;

	const { data } = useSelector((state: any) => state?.userProfile);

	const { conversionRate, usdCurrencyToggle, isUsdCurrency } = useSelector(
		(state: any) => state.usdCurrencyToggle,
	);

	const [selectAllLeave, setSelectAllLeave] = useState(false);
	const [selectAllGratuity, setSelectAllGratuity] = useState(false);
	const [deleteEmployeeId, setDeleteEmployeeId] = useState<string | null>(null);
	const [deletePayrollId, setDeletePayrollId] = useState<string | null>(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);

	const FormattedValue = (value: any) => {
		if (!usdCurrencyToggle && isUsdCurrency) {
			return formatNumber(Number(value), data?.CompanyCurrencies, true);
		} else {
			return formatNumber(
				Number(value * (conversionRate ?? 1)),
				data?.CompanyCurrencies,
			);
		}
	};

	const handleSelectAll = (type: 'leave' | 'gratuity', checked: boolean) => {
		if (type === 'leave') {
			setSelectAllLeave(checked);
			handleBulkCheckboxChange('leave', checked);
		} else {
			setSelectAllGratuity(checked);
			handleBulkCheckboxChange('gratuity', checked);
		}
	};
	const openDeleteModal = (employeeId: string, payrollId: string) => {
		setDeleteEmployeeId(employeeId);
		setDeletePayrollId(payrollId);
		setIsDeleteModalOpen(true);
	};
	const cancelDeleteModal = () => {
		setIsDeleteModalOpen(false);
		setDeleteEmployeeId(null);
		setDeletePayrollId(null);
	};

	const deleteHandler = async () => {
		if (!deleteEmployeeId || !deletePayrollId) return;

		try {
			setIsDeleteLoading(true);

			await payrollApi.deleteAdvancePayment(deleteEmployeeId, deletePayrollId);
			toastText('Advance payment deleted successfully', 'success');
			cancelDeleteModal();
			fetchLeaveGratuityData(deletePayrollId);
		} catch (error: any) {
			const message =
				error?.response?.data?.message ||
				'Something went wrong while deleting advance payment';

			toastText(message, 'error');
		} finally {
			setIsDeleteLoading(false);
		}
	};

	const columns: ColumnsType<any> = [
		{
			title: 'Employee Name',
			dataIndex: 'employeeName',
			key: 'employeeName',
			width: 220,
			fixed: 'left',
			className: 'bg-white',
			render: (_: any, record: any) => (
				<>
					<span>{record.employeeName}</span> |{' '}
					<span className="color-purple">{record.employeeCode}</span>
					{record.entrySource === 'ADVANCE_PAYMENT' && (
						<Tag color="purple" style={{ marginLeft: 8 }}>
							Advance Payment
						</Tag>
					)}
				</>
			),
		},
		{
			title: 'Last Working Day',
			dataIndex: 'lastWorkingDay',
			className: 'bg-white',
			key: 'lastWorkingDay',
			width: 150,
			render: (text: any) => (text ? formatToDateOnly(text) : '-'),
		},
		{
			title: (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						gap: 8,
					}}
				>
					{!onlyView && (
						<Checkbox
							checked={selectAllLeave}
							onChange={(e) => handleSelectAll('leave', e.target.checked)}
						/>
					)}
					<span>Add Leave Pay</span>
				</div>
			),
			dataIndex: 'isLeaveIncluded',
			key: 'isLeaveIncluded',
			width: 200,
			align: 'center',
			className: 'bg-white',
			render: (_: any, record: any) => (
				<Checkbox
					checked={record.alreadyLeavePaid || record.isLeaveIncluded || false}
					disabled={onlyView || record.alreadyLeavePaid}
					onChange={(e) =>
						handleCheckboxChange(
							record.employeeId,
							e.target.checked,
							'isLeaveIncluded',
						)
					}
				/>
			),
		},
		{
			title: 'Entitlement (Days)',
			dataIndex: 'leaveDays',
			key: 'leaveDays',
			width: 130,
			render: (text: any) => `${text ?? 0} days`,
			className: 'bg-white',
		},
		{
			title: 'Entitlement (Amount)',
			dataIndex: 'leaveAmount',
			key: 'leaveAmount',
			width: 150,
			className: 'bg-white',
			render: (value: any) => FormattedValue(value),
		},
		...(handlePaidLeaveDaysChange
			? [
					{
						title: 'Paid Days',
						key: 'paidLeaveDays',
						width: 100,
						className: 'bg-white',
						render: (_: any, record: any) => {
							if (
								record.entrySource !== 'ADVANCE_PAYMENT' ||
								!record.isLeaveIncluded ||
								onlyView
							) {
								return '-';
							}
							const entitledDays = Number(record.leaveDays) || 0;
							const minPaidDays = entitledDays > 0 ? 1 : 0;
							const raw =
								record.paidLeaveDays != null
									? record.paidLeaveDays
									: entitledDays;
							const value = Math.max(minPaidDays, raw);
							return (
								<InputNumber
									min={minPaidDays}
									max={entitledDays}
									value={value}
									onChange={(val) =>
										handlePaidLeaveDaysChange(
											record.employeeId,
											Number(val) ?? 0,
										)
									}
									style={{ width: '100%' }}
								/>
							);
						},
					},
					{
						title: 'Paid Amount',
						key: 'paidLeaveAmount',
						width: 130,
						className: 'bg-white',
						render: (_: any, record: any) => {
							if (
								record.entrySource !== 'ADVANCE_PAYMENT' ||
								!record.isLeaveIncluded
							)
								return '-';
							const amt = record.paidLeaveAmount ?? record.leaveAmount ?? 0;
							return FormattedValue(amt);
						},
					},
					{
						title: 'Remaining',
						key: 'remainingLeave',
						width: 150,
						className: 'bg-white',
						render: (_: any, record: any) => {
							if (
								record.entrySource !== 'ADVANCE_PAYMENT' ||
								!record.isLeaveIncluded
							)
								return '-';
							const days =
								(Number(record.leaveDays) || 0) -
								(record.paidLeaveDays ?? Number(record.leaveDays) ?? 0);
							const amount =
								(Number(record.leaveAmount) || 0) -
								(record.paidLeaveAmount ?? Number(record.leaveAmount) ?? 0);
							return (
								<span>
									{days} days / {FormattedValue(amount)}
								</span>
							);
						},
					},
				]
			: []),
		{
			title: (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						gap: 8,
					}}
				>
					{!onlyView && (
						<Checkbox
							checked={selectAllGratuity}
							onChange={(e) => handleSelectAll('gratuity', e.target.checked)}
						/>
					)}
					<span>Add Gratuity Pay</span>
				</div>
			),
			dataIndex: 'isGratuityIncluded',
			key: 'isGratuityIncluded',
			className: 'bg-white',
			width: 220,
			align: 'center',
			render: (_: any, record: any) => (
				<Checkbox
					checked={
						record.alreadyGratuityPaid || record.isGratuityIncluded || false
					}
					disabled={onlyView || record.alreadyGratuityPaid}
					onChange={(e) =>
						handleCheckboxChange(
							record.employeeId,
							e.target.checked,
							'isGratuityIncluded',
						)
					}
				/>
			),
		},
		{
			title: 'Amount',
			dataIndex: 'gratuityAmount',
			key: 'gratuityAmount',
			width: 150,
			render: (value: any) => FormattedValue(value),
			className: 'bg-white',
		},
		{
			title: 'Action',
			key: 'action',
			width: 100,
			fixed: 'right',
			align: 'center',
			className: 'bg-white',
			render: (_: any, record: any) => {
				const isDisabled = record.entrySource !== 'ADVANCE_PAYMENT' || onlyView;
				return (
					<Tooltip
						title={
							isDisabled
								? 'Record for advance payment can only be deleted'
								: 'Delete advance payment'
						}
					>
						{!isDisabled ? (
							<p
								className="cursor-pointer flex align-center justify-center"
								style={{
									fontSize: 18,
									cursor: 'pointer',
								}}
								onClick={() =>
									openDeleteModal(record.employeeId, record.payrollId)
								}
							>
								<DeleteActionSvg />
							</p>
						) : (
							<p className="cursor-not-allowed flex align-center justify-center">
								<DeleteActionSvg color="#928F8F" />
							</p>
						)}
					</Tooltip>
				);
			},
		},
	];

	return (
		<>
			<div className="payroll-steps-table">
				<Table
					bordered
					dataSource={employeeData}
					columns={columns}
					pagination={false}
					scroll={{
						x: 1700,
						y: showSteps ? 'calc(100vh - 450px)' : 'calc(100vh - 375px)',
					}}
					rowKey={(record) => record.employeeId}
					loading={isLoading}
					onChange={tableChangeHandler}
				/>
			</div>
			<ConfirmDelete
				handleCancel={cancelDeleteModal}
				handleOk={cancelDeleteModal}
				isModalOpen={isDeleteModalOpen}
				deleteHandler={deleteHandler}
				isLoading={isDeleteLoading}
			/>
		</>
	);
};

export default LeaveGratuityPayTable;
